import { useState } from 'react';
import { format } from 'date-fns';
import { CalendarClock } from 'lucide-react';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { useUIStore } from '@/state/uiStore';
import clsx from 'clsx';
import { isInsideCancelWindow, studioDayKey, studioWallTimeToIso } from '@atink/domain';
import { topupMoveDate } from '@/domain/topupChaseFlow';
import { liveMoveRequest, sendTopupMoveToOps } from '@/domain/topupMoveRequest';
import type { TopupMovePay } from '@/api/intents';
import { aud } from '@/utils/format';
import { formatStudioDay, studioTime } from '@/utils/studioDay';
import DepositEmailField, { depositEmailFor } from '@/components/deposits/DepositEmailField';

/**
 * "Move date" on a missed top-up (B4, 75my; diagram 02; Anas 1 Oct Q4). The Agent
 * picks the new day and takes the partial (at least $50) then and there (L5,
 * D02-23 / D05-23), the two ways B4's other flows offer:
 *   - card over the phone now: the partial is paid on the call and the booking
 *     moves straight away;
 *   - text the client a pay link: the server texts it now, naming the new date;
 *     the new date is held (a ghost) until it's paid, and if it isn't within 24
 *     hours both dates are released and what they've paid stays on file.
 * Card over the phone is a staff-taken payment, so it needs the client's email
 * (D05-11): the one on file, or one typed here (the server fills an empty one).
 */
const METHODS: ReadonlyArray<{ key: TopupMovePay; label: string; hint: string }> = [
  { key: 'card-phone', label: 'Card over the phone now', hint: 'Moves it now' },
  { key: 'text-link', label: 'Text the client a pay link', hint: '24h hold' },
];

const METHOD_TESTID: Record<TopupMovePay, string> = { 'card-phone': 'card', 'text-link': 'link' };
export default function TopupMovePanel({
  planId,
  customerId,
  onDone,
}: {
  planId: string;
  customerId: string;
  onDone: () => void;
}) {
  const plan = useBrainStore((s) => s.topupPlans[planId]);
  const current = useBrainStore((s) => (plan?.bookingId ? s.bookings[plan.bookingId] : undefined));
  const customer = useBrainStore((s) => s.customers[customerId]);
  const [typedEmail, setTypedEmail] = useState('');
  const clockIso = useSimulationStore((s) => s.clockIso);
  const pushNotification = useUIStore((s) => s.pushNotification);
  const [day, setDay] = useState('');
  // No default: taking money on the call or texting a link is the Agent's choice.
  const [pay, setPay] = useState<TopupMovePay | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  // The new date keeps the session's start time, both read as studio
  // (Melbourne) wall time, whatever the browser's zone (D01-17).
  const newStartIso = (() => {
    if (!day) return undefined;
    const time = current ? studioTime(current.start) : '11:00';
    return new Date(studioWallTimeToIso(day, `${time}:00`)).toISOString();
  })();
  // D01-17: inside 7 days of the appointment a move is Ops' call. The Agent
  // sends it to Ops with the date the client asked for, instead of a dead end.
  const insideSevenDays = current ? isInsideCancelWindow(current.start, clockIso) : false;
  const sentToOpsIso = plan ? liveMoveRequest(plan, current, clockIso) : undefined;

  const sendToOps = async () => {
    if (!newStartIso || saving) return;
    setSaving(true);
    setMessage(null);
    const res = await sendTopupMoveToOps(planId, newStartIso);
    setSaving(false);
    if (!res.ok) {
      setMessage(res.message);
      return;
    }
    pushNotification({
      intensity: 'high',
      title: 'Sent to Ops',
      body: `The appointment is inside 7 days, so Ops decides. They have the date the client asked for: ${formatStudioDay(res.requestedStartIso, 'EEE d MMM')}.`,
      ttlMs: 8000,
      customerId,
    });
  };

  const cardEmail = depositEmailFor(customer, typedEmail);
  const ready = !!newStartIso && !!pay && !(pay === 'card-phone' && !cardEmail);

  const send = async () => {
    if (!newStartIso || !pay || !ready || saving) return;
    setSaving(true);
    setMessage(null);
    const res = await topupMoveDate(planId, newStartIso, pay, pay === 'card-phone' ? cardEmail : undefined);
    setSaving(false);
    if (!res.ok) {
      setMessage(res.message);
      return;
    }
    const newDay = formatStudioDay(newStartIso, 'EEE d MMM');
    const until = format(new Date(res.lapsesAt), 'EEE d MMM, h:mm a');
    pushNotification(
      res.landed
        ? {
            intensity: 'high',
            title: 'Date moved',
            body: `${aud(res.partialAud)} taken by card. The booking is now ${newDay}.`,
            ttlMs: 8000,
            customerId,
          }
        : res.texted === false
          ? {
              intensity: 'high',
              title: 'New date held · no mobile',
              body: `There's no mobile on file, so no link was texted. The ${aud(res.partialAud)} partial is due by ${until}; send the client their deposit link another way.`,
              ttlMs: 10000,
              customerId,
            }
          : {
              intensity: 'high',
              title: res.alreadyPending ? 'Pay link sent again' : 'Pay link sent',
              body: `We've texted the client a link for the ${aud(res.partialAud)} partial. ${newDay} is held until ${until}; the old date stands until it's paid.`,
              ttlMs: 8000,
              customerId,
            },
    );
    onDone();
  };

  if (insideSevenDays) {
    return (
      <div
        className="mt-2 flex flex-wrap items-center gap-2 rounded-md border border-gold-dim/40 bg-ink-deep/60 p-2"
        data-testid={`queue-topup-move-panel-${customerId}`}
      >
        <CalendarClock size={12} className="text-gold-bright" />
        <span className="font-serif text-[12px] text-cream-soft">Date the client wants</span>
        <input
          type="date"
          value={day}
          min={studioDayKey(clockIso)}
          onChange={(e) => setDay(e.target.value)}
          className="bg-ink-black border border-gold-dim/40 rounded px-2 py-1 text-xs text-cream"
          data-testid={`queue-topup-move-date-${customerId}`}
        />
        <button
          type="button"
          onClick={() => void sendToOps()}
          disabled={!newStartIso || saving}
          className="rounded-md border border-gold-bright/50 px-2 py-1 text-[11px] uppercase tracking-widest text-gold-bright hover:bg-gold/10 disabled:opacity-40"
          data-testid={`queue-topup-move-send-ops-${customerId}`}
        >
          {saving ? 'Sending…' : sentToOpsIso ? 'Send the new date to Ops' : 'Send to Ops'}
        </button>
        <button type="button" onClick={onDone} className="btn-quiet text-[11px]">
          {sentToOpsIso ? 'Done' : 'Cancel'}
        </button>
        {sentToOpsIso ? (
          <p className="basis-full font-serif text-[12px] text-mint" data-testid={`queue-topup-move-sent-ops-${customerId}`}>
            Sent to Ops · the client asked for {formatStudioDay(sentToOpsIso, 'EEE d MMM')}. Ops decides; nothing moves until they do.
          </p>
        ) : null}
        {message ? (
          <p className="basis-full font-serif text-[12px] text-orange" data-testid={`queue-topup-move-message-${customerId}`}>
            {message}
          </p>
        ) : (
          <p className="basis-full font-serif text-[11px] text-cream-faint" data-testid={`queue-topup-move-hint-${customerId}`}>
            The appointment is inside 7 days, so moving it is Ops' call. Send them the date the client wants; they'll see it on their Top-up extensions list.
          </p>
        )}
      </div>
    );
  }

  return (
    <div
      className="mt-2 flex flex-wrap items-center gap-2 rounded-md border border-gold-dim/40 bg-ink-deep/60 p-2"
      data-testid={`queue-topup-move-panel-${customerId}`}
    >
      <CalendarClock size={12} className="text-gold-bright" />
      <span className="font-serif text-[12px] text-cream-soft">New date</span>
      <input
        type="date"
        value={day}
        min={studioDayKey(clockIso)}
        onChange={(e) => setDay(e.target.value)}
        className="bg-ink-black border border-gold-dim/40 rounded px-2 py-1 text-xs text-cream"
        data-testid={`queue-topup-move-date-${customerId}`}
      />
      <div className="basis-full flex flex-wrap gap-1.5" role="radiogroup" aria-label="Take the partial">
        {METHODS.map((m) => (
          <button
            key={m.key}
            type="button"
            role="radio"
            aria-checked={pay === m.key}
            onClick={() => setPay(m.key)}
            data-testid={`queue-topup-move-method-${METHOD_TESTID[m.key]}-${customerId}`}
            className={clsx(
              'flex items-center gap-2 rounded-md border px-2 py-1 text-left transition',
              pay === m.key ? 'border-gold-bright bg-gold/10' : 'border-gold-dim/30 hover:border-gold-bright/40',
            )}
          >
            <span className="font-serif text-[12px] text-cream">{m.label}</span>
            <span className="font-inter text-[9px] uppercase tracking-widest text-gold-bright">{m.hint}</span>
          </button>
        ))}
      </div>
      {pay === 'card-phone' ? (
        <DepositEmailField customer={customer} value={typedEmail} onChange={setTypedEmail} className="basis-full" />
      ) : null}
      <button
        type="button"
        onClick={() => void send()}
        disabled={!ready || saving}
        className="rounded-md border border-gold-bright/50 px-2 py-1 text-[11px] uppercase tracking-widest text-gold-bright hover:bg-gold/10 disabled:opacity-40"
        data-testid={`queue-topup-move-send-${customerId}`}
      >
        {saving
          ? pay === 'card-phone'
            ? 'Taking payment…'
            : 'Sending…'
          : pay === 'card-phone'
            ? 'Take partial · move date'
            : pay === 'text-link'
              ? 'Text pay link · hold date'
              : 'Pick how the partial is paid'}
      </button>
      <button type="button" onClick={onDone} className="btn-quiet text-[11px]">
        Cancel
      </button>
      {message ? (
        <p className="basis-full font-serif text-[12px] text-orange" data-testid={`queue-topup-move-message-${customerId}`}>
          {message}
        </p>
      ) : (
        <p className="basis-full font-serif text-[11px] text-cream-faint" data-testid={`queue-topup-move-hint-${customerId}`}>
          {pay === 'card-phone'
            ? 'The partial (at least $50) is taken by card now, and the booking moves straight away.'
            : pay === 'text-link'
              ? 'We text the client a link for the partial (at least $50) now. The new date holds for 24 hours; if it isn’t paid by then, both dates are released and what they’ve paid stays on file.'
              : 'The client pays a partial of at least $50 to move the date: by card now, or on a link we text them.'}
        </p>
      )}
    </div>
  );
}
